import { z } from 'zod';
import { boardParams, endpoint, id, KIOSK_PHOTO_TYPES, LIMITS } from './common';

// What a board's kiosk display shows: a note and photos.

export const updateKioskMessage = endpoint({
    method: 'POST',
    path: '/updateKioskMessage',
    body: z.object({
        boardId: id,
        kioskMessage: z.string().max(LIMITS.kioskMessage, `kioskMessage must be a string of at most ${LIMITS.kioskMessage} characters`),
    }),
    response: z.object({ boardId: id, kioskMessage: z.string() }),
});

// Photos added by link. (Uploaded photos are separate; see below.)
export const updateKioskPhotos = endpoint({
    method: 'POST',
    path: '/updateKioskPhotos',
    body: z.object({
        boardId: id,
        photoUrls: z.array(
            z.string().max(LIMITS.kioskPhotoLinkLength).refine((url) => url.startsWith('http://') || url.startsWith('https://'), 'must be an http(s) URL'),
        ).max(LIMITS.kioskPhotoLinks, `photoUrls must be an array of up to ${LIMITS.kioskPhotoLinks} http(s) URLs`),
    }),
    response: z.object({ boardId: id, photoUrls: z.array(z.string()) }),
});

// Uploaded photos go from the browser straight to storage in three steps: ask for an upload address, send the file
// there, then register it so it is checked and recorded.
export const kioskPhoto = z.object({
    id: z.string(),
    contentType: z.string(),
    url: z.string(),               // a short-lived link to the stored image
});
export type KioskPhoto = z.infer<typeof kioskPhoto>;

export const getKioskPhotos = endpoint({
    method: 'GET',
    path: '/boards/:boardId/kioskPhotos',
    params: boardParams,
    response: z.object({ photos: z.array(kioskPhoto) }),
});

export const createKioskPhotoUploadUrl = endpoint({
    method: 'POST',
    path: '/boards/:boardId/kioskPhotos/uploadUrl',
    params: boardParams,
    body: z.object({
        contentType: z.enum(KIOSK_PHOTO_TYPES, { errorMap: () => ({ message: `contentType must be one of: ${KIOSK_PHOTO_TYPES.join(', ')}` }) }),
        size: z.number().int().positive().max(LIMITS.kioskPhotoBytes, `Photo must be at most ${LIMITS.kioskPhotoBytes / (1024 * 1024)}MB`),
    }),
    response: z.object({ photoId: id, uploadUrl: z.string() }),
});

export const registerKioskPhoto = endpoint({
    method: 'POST',
    path: '/boards/:boardId/kioskPhotos',
    params: boardParams,
    body: z.object({ photoId: z.string().uuid('photoId must be a UUID') }),
    response: z.object({ photoId: id }),
});

export const deleteKioskPhoto = endpoint({
    method: 'POST',
    path: '/deleteKioskPhoto',
    body: z.object({ photoId: id }),
    response: z.object({ photoId: id }),
});
